<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuasar, type QDateProps, type QInputProps } from 'quasar';
import { isDateValid } from '@/functions/isDateValid';
import DateInput from '@/components/DateInput.vue';

defineProps<{
    loading: boolean;
}>();

const $q = useQuasar();
const startDate = ref<string>('');
const endDate = ref<string>('');

const emit = defineEmits<{(event: 'submit:form', startDate: string, endDate: string): void;}>();
const ruleDateOrEmpty = (val:string) => (!val || isDateValid(val, 'MM/DD/YYYY') || 'Please enter a valid date in the format MM/DD/YYYY');
const ruleNoFutureDate = (val:string) => (!val || new Date(val) <= new Date() || 'Date must not be in the future');

const dateOptions = (dateString: string, isStartDate: boolean) => {
    if (!isStartDate && !startDate.value) {
        return true;
    }

    const usableDate = new Date(dateString);
    const meetsMinimumDate = isStartDate ? true : usableDate >= new Date(startDate.value);
    const meetsMaximumDate = usableDate <= new Date();

    return meetsMinimumDate && meetsMaximumDate;
};

// Handle actual form submission in the parent component for simplicity
const validateForm = () => {
    if (!startDate.value && !endDate.value) {
        emit('submit:form', startDate.value, endDate.value);
        return;
    }

    if (!startDate.value || !endDate.value) {
        $q.notify({
            message: 'Please enter both a start and end date',
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
        });
        return;
    }

    if (new Date(endDate.value) < new Date(startDate.value)) {
        $q.notify({
            message: 'End date must be after start date',
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
        });
        return;
    }

    emit('submit:form', startDate.value, endDate.value);
};

const inputProps = computed((): Omit<QInputProps, 'modelValue'> => ({
    rules: [ruleDateOrEmpty, ruleNoFutureDate],
    outlined: true,
    hint: 'Date Format: MM/DD/YYYY',
    hideHint: true,
    lazyRules: true,
    clearable: true,
}));

const startDateProps = computed((): Omit<QDateProps, 'modelValue'> => ({
    options: (date: string) => dateOptions(date, true),
}));

const endDateProps = computed((): Omit<QDateProps, 'modelValue'> => ({
    options: (date: string) => dateOptions(date, false),
}));

</script>
<template>
    <q-form
        class="pass-rate-form print-hide q-py-lg bg-white q-mb-lg q-mt-none q-mx-sm"
        :class="$q.screen.gt.xs ? 'q-px-xl' : 'q-px-lg'"
        @submit="validateForm"
    >
        <div class="form-title text-weight-bold text-left q-pb-sm">
            Select A Date Range
        </div>
        <div class="row inputs q-gutter-y-md">
            <div
                class="input-wrapper col-12 col-sm-6"
                align="left"
            >
                <div class="input-wrapper-inner">
                    <div
                        class="input-label q-pb-xs"
                    >
                        From
                    </div>
                    <DateInput
                        :model-value="startDate"
                        :input-props="inputProps"
                        :date-props="startDateProps"
                        :is-icon-prepended="true"
                        :name="'start-date'"
                        :class="'q-pa-none q-mr-sm exam-date-input col q-mb-md'"
                        :aria-label="'Please enter a start date in the format MM/DD/YYYY'"
                        :title="'Please enter a start date in the format MM/DD/YYYY'"
                        :placeholder="'MM/DD/YYYY'"
                        :role="'textbox'"
                        @update:model-value="startDate = $event"
                    />
                </div>
            </div>
            <div
                class="input-wrapper col-12 col-sm-6"
                :align="$q.screen.gt.xs ? 'right' : 'left'"
            >
                <div class="input-wrapper-inner">
                    <div
                        class="input-label q-pb-xs"
                        align="left"
                    >
                        To
                    </div>
                    <DateInput
                        :model-value="endDate"
                        :input-props="inputProps"
                        :date-props="endDateProps"
                        :is-icon-prepended="true"
                        :name="'end-date'"
                        :class="'q-pa-none q-mr-sm exam-date-input col q-mb-md'"
                        :aria-label="'Please enter an end date in the format MM/DD/YYYY'"
                        :title="'Please enter an end date in the format MM/DD/YYYY'"
                        :placeholder="'MM/DD/YYYY'"
                        @update:model-value="endDate = $event"
                    />
                </div>
            </div>
        </div>
        <div :align="$q.screen.gt.xs ? 'center' : 'left'">
            <q-btn
                id="submit-btn"
                label="Run Report"
                type="submit"
                class="fsum-button-dark-blue q-mt-lg q-mb-md"
                no-caps
                :loading="loading"
            />
        </div>
    </q-form>
</template>
<style scoped lang="scss">
.pass-rate-form {
    border-radius: 4px;

    .input-wrapper-inner {
        max-width: 250px;
        min-width: 125px;
    }
}

</style>
